import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FormRenderer from "./FormRenderer.jsx";
import Md from "./Markdown.jsx";
import { readFileAsDataUrl, fmtTime, BRAND, GOLD } from "./helpers.js";

/**
 * One packet form, for either the new hire (mode="employee") or HR (mode="hr").
 * `io` adapts the API calls: { load, save(values), stamp(key), submit?, upload?(data), fileUrl(id), reopen?(reason) }.
 */
export default function PacketFormView({ mode, io, backTo, nextTo, onDone }) {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [missing, setMissing] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState("");

  const load = () => io.load().then((d) => { setP(d); setMissing(null); }).catch((e) => setError(e.message));
  useEffect(() => { setP(null); setError(""); setNotice(""); load(); window.scrollTo(0, 0); }, [io.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !p) return <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-800 rounded-xl p-5">{error} <div className="mt-3"><Link to={backTo} className="underline">Back</Link></div></div>;
  if (!p) return <div className="text-gray-500">Loading form…</div>;

  const f = p.form;
  const setData = (data) => setP((cur) => ({ ...cur, data }));

  const onChange = async (key, value) => {
    setData({ ...p.data, [key]: value });
    try {
      const r = await io.save({ [key]: value });
      setP((cur) => ({ ...cur, missing: r.missing }));
    } catch (e) { setError(e.message); }
  };
  const onStamp = async (key) => {
    setBusyKey(key); setError("");
    try {
      const r = await io.stamp(key);
      setP((cur) => ({ ...cur, data: { ...cur.data, [key]: r.stamp }, missing: r.missing }));
    } catch (e) { setError(e.message); }
    finally { setBusyKey(null); }
  };
  const submit = async () => {
    setSubmitting(true); setError(""); setMissing(null);
    try {
      await io.submit();
      if (onDone) onDone();
      if (nextTo) nav(nextTo); else nav(backTo);
    } catch (e) {
      if (e.data?.missing) setMissing(e.data.missing); else setError(e.message);
    } finally { setSubmitting(false); }
  };
  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind); setError(""); setNotice("");
    try {
      const file_data = await readFileAsDataUrl(file);
      await io.upload({ kind, file_name: file.name, mime_type: file.type || "application/octet-stream", file_data });
      setNotice(`${file.name} uploaded.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setUploading(""); }
  };
  const reopen = async () => {
    const reason = window.prompt("Why are you re-opening this form for the employee? (recorded in the audit trail)");
    if (!reason) return;
    try { await io.reopen(reason); await load(); setNotice("Form re-opened. The employee will need to initial and sign it again."); }
    catch (e) { setError(e.message); }
  };

  const signed = p.emp_status === "signed";

  return (
    <div className="max-w-4xl mx-auto pb-24">
      <Link to={backTo} className="text-sm hover:underline" style={{ color: BRAND }}>← {mode === "hr" ? "Back to packet" : "Back to my packet"}</Link>

      <div className="bg-white border rounded-2xl shadow-sm mt-3 overflow-hidden">
        <div className="px-5 sm:px-8 pt-6 pb-4 border-b-2" style={{ borderColor: "#C9A84C" }}>
          <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>{f.num}{f.part ? ` · ${f.part}` : ""}</div>
          <h1 className="text-2xl font-bold mt-1" style={{ color: "#1B3A6B" }}>{f.title}</h1>
          {f.sub && <p className="text-sm italic text-gray-500 mt-1"><Md text={f.sub} /></p>}
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            {signed && <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">✓ Completed {fmtTime(p.emp_signed_at)}</span>}
            {!signed && mode === "hr" && p.form.id !== "f22" && <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">Employee has not finished this form</span>}
            {p.locked && mode === "employee" && !signed && !p.waiting_hr && p.prev_open && <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Finish {p.prev_open} first</span>}
            {p.waiting_hr && <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Waiting for Human Resources to complete and sign this form</span>}
          </div>
        </div>

        {mode === "hr" && !p.signer_adopted && Object.keys(p.stamps || {}).length > 0 && (
          <div className="mx-5 sm:mx-8 mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
            Adopt your HR signature before you countersign. <Link to="/admin/onboarding/signature" className="underline font-semibold">Adopt signature</Link>
          </div>
        )}
        {p.ack && mode === "employee" && (
          <div className="mx-5 sm:mx-8 mt-4 text-sm border-l-4 bg-slate-50 px-3 py-2" style={{ borderColor: BRAND }}>
            This disclosure stands alone, as federal law requires. Read it, then tap <b>I have read this disclosure</b>. You authorize the background check on the next form.
          </div>
        )}

        <div className="px-5 sm:px-8 py-6">
          <FormRenderer form={f} data={p.data} mode={mode} locked={p.locked} images={p.images} stamps={p.stamps} employee={p.employee}
            onChange={onChange} onStamp={onStamp} busyKey={busyKey} />
        </div>

        {(p.uploads?.length > 0 || p.files?.length > 0) && (
          <div className="px-5 sm:px-8 pb-6">
            <h3 className="font-bold mb-2" style={{ color: BRAND }}>Attachments</h3>
            {p.uploads?.length > 0 && <p className="text-sm text-gray-500 mb-2">Optional — take a photo or attach a PDF. You can also hand these to HR.</p>}
            <div className="grid sm:grid-cols-2 gap-2">
              {p.uploads?.map((u) => (
                <label key={u.kind} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <span>{u.label}</span>
                  <span className="text-xs font-semibold" style={{ color: BRAND }}>{uploading === u.kind ? "Uploading…" : "Add file"}</span>
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(u.kind, e.target.files?.[0])} />
                </label>
              ))}
            </div>
            {p.files?.length > 0 && (
              <ul className="mt-3 text-sm space-y-1">
                {p.files.map((x) => (
                  <li key={x.id} className="flex justify-between gap-2 border-b py-1">
                    <span>✓ {x.label}: <a href={io.fileUrl(x.id)} target="_blank" rel="noreferrer" className="underline">{x.filename}</a></span>
                    <span className="text-xs text-gray-500">{fmtTime(x.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {notice && <div className="mt-4 text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3">{notice}</div>}
      {error && <div className="mt-4 text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">{error}</div>}
      {missing && (
        <div className="mt-4 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="font-semibold text-amber-900 mb-1">Still needed before you can finish this form:</div>
          <ul className="list-disc ml-5 text-amber-900">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      {/* Sticky action bar (thumb-reachable on phones) */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-auto lg:right-28 lg:bottom-6 bg-white/95 backdrop-blur border-t lg:border lg:rounded-xl shadow-lg pl-4 pr-24 lg:pr-4 py-3 flex items-center justify-between gap-3 z-30">
        <div className="text-xs text-gray-600">
          {mode === "employee" && !p.locked && (p.missing?.length ? `${p.missing.length} item${p.missing.length === 1 ? "" : "s"} left on this form` : p.ack ? "Read the disclosure above" : "All items complete")}
          {mode === "employee" && signed && "Completed"}
          {mode === "hr" && (p.missing?.length ? `${p.missing.length} HR item${p.missing.length === 1 ? "" : "s"} left` : "HR items complete")}
          {mode === "hr" && p.emp_missing?.length > 0 && ` · employee: ${p.emp_missing.length} left`}
        </div>
        <div className="flex gap-2">
          {mode === "employee" && !p.locked && (
            <button type="button" onClick={submit} disabled={submitting} className="px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-60" style={{ backgroundColor: BRAND }}>
              {submitting ? "Saving…" : p.ack ? "I have read this disclosure" : "Finish this form"}
            </button>
          )}
          {mode === "employee" && signed && nextTo && <Link to={nextTo} className="px-5 py-2.5 rounded-lg text-white font-semibold" style={{ backgroundColor: BRAND }}>Next form →</Link>}
          {mode === "hr" && signed && io.reopen && !p.locked && (
            <button type="button" onClick={reopen} className="px-3 py-2 rounded-lg border text-sm text-red-700 border-red-200">Re-open for employee</button>
          )}
          {mode === "hr" && <Link to={backTo} className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: BRAND }}>Done</Link>}
        </div>
      </div>
    </div>
  );
}
